import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Bell, CalendarDays, MessageSquareWarning, 
  Wallet, CreditCard, UserRoundCheck, Car, Building2, 
  FileText, Image as ImageIcon, BriefcaseBusiness, BarChart, Settings, LogOut, Shield, QrCode
} from 'lucide-react';
import logo from "../../assets/Sankrutilogo.jpeg";
import api from "../../api/axios";
import styles from './AdminSidebar.module.css';

const adminMenuGroups = [
  {
    title: 'MAIN',
    items: [
      { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, exact: true },
    ]
  },
  {
    title: 'SOCIETY MANAGEMENT',
    items: [
      { name: 'Residents', path: '/admin/residents', icon: Users },
      { name: 'Complaints', path: '/admin/complaints', icon: MessageSquareWarning },
      { name: 'Maintenance', path: '/admin/maintenance', icon: Wallet },
      { name: 'Payments', path: '/admin/payments', icon: CreditCard },
      { name: 'Visitors', path: '/admin/visitors', icon: UserRoundCheck },
      { name: 'Vehicles', path: '/admin/vehicles', icon: Car },
      { name: 'Gate Passes', path: '/admin/gate-passes', icon: QrCode },
      { name: 'Security Staff', path: '/admin/security-staff', icon: Shield },
      { name: 'Security Logs', path: '/admin/security-logs', icon: Shield },
      { name: 'Amenities & Bookings', path: '/admin/amenities', icon: Building2 },
    ]
  },
  {
    title: 'COMMUNITY',
    items: [
      { name: 'Committee Leader', path: '/admin/committee', icon: Users },
      { name: 'Events', path: '/admin/events', icon: CalendarDays },
      { name: 'Notices', path: '/admin/notices', icon: Bell },
      { name: 'Polls & Voting', path: '/admin/polls', icon: FileText },
      { name: 'Gallery', path: '/admin/gallery', icon: ImageIcon },
      { name: 'Suggestions', path: '/admin/suggestions', icon: FileText },
    ]
  },
  {
    title: 'RESOURCES',
    items: [
      { name: 'Documents', path: '/admin/documents', icon: FileText },
      { name: 'Reports', path: '/admin/reports', icon: BarChart },
    ]
  },
  {
    title: 'ACCOUNT',
    items: [
      { name: 'Settings', path: '/admin/settings', icon: Settings },
    ]
  }
];

const AdminSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotificationsCount = async () => {
      try {
        const { data } = await api.get('/users/notifications');
        const count = data.filter(n => !n.read).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to fetch notifications for sidebar', error);
      }
    };
    
    fetchNotificationsCount();
    window.addEventListener('notificationsUpdated', fetchNotificationsCount);
    return () => window.removeEventListener('notificationsUpdated', fetchNotificationsCount);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <>
      <div className={`${styles.sidebarOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}></div>
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
        <div className={styles.logoArea} onClick={() => { navigate('/'); if(onClose) onClose(); }} style={{ cursor: 'pointer' }} title="Go to Website Home">
          <img src={logo} alt="Sanskriti Logo" className={styles.logoImage} />
        </div>
        
        <nav className={styles.navLinks}>
          {adminMenuGroups.map((group, groupIndex) => (
            <div key={groupIndex} className={styles.navGroup}>
              {group.title && <div className={styles.groupTitle}>{group.title}</div>}
              
              {group.items.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                // Override badge for Notifications dynamically
                const displayBadge = link.name === 'Notifications' ? (unreadCount > 0 ? unreadCount : null) : link.badge;
                
                return (
                  <button
                    key={link.path}
                    className={`${styles.navItem} ${isActive ? styles.active : ""}`}
                    onClick={() => { handleNavigate(link.path); if(onClose) onClose(); }}
                  >
                    <Icon className={styles.icon} size={16} strokeWidth={2} />
                    <span>{link.name}</span>
                    {displayBadge && <span className={styles.badge}>{displayBadge}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
